import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminSidebar from './SuperAdminSidebar';
import TopNavbar from '../TopNavbar';
import DashboardContent from './SuperAdminDashboardContent';
import CompaniesContent from './SuperAdminCompaniesContent';
import UsersContent from './SuperAdminUsers/SuperAdminUsersContent';
import SettingsContent from './SuperAdminSettingsContent';
import Footer from '../Footer';
import ProgressScreen from '../ProgressScreen';
import DataContent from '../DataContent';
import HomeContent from '../HomeContent';
import SuperAdminExtractionsContent from './SuperAdminExtractionsContent';

const SuperAdminDashboard = ({ userInfo, setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Extraction flow state
  const [extractionState, setExtractionState] = useState({
    isInExtractionFlow: false,
    extractionFiles: [],
    extractionSections: [],
    extractionProgress: null,
    currentProgressData: null,
    jobId: null
  });
  
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [extractedData, setExtractedData] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showBackArrow, setShowBackArrow] = useState(false);
  const [fullscreenMode, setFullscreenMode] = useState(false);
  const [activeStatement, setActiveStatement] = useState('Income Statement');
  const [isInDataView, setIsInDataView] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('access_token');
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  };

  // Reset states when switching sections
  useEffect(() => {
    if (activeSection === 'dashboard') {
      setFullscreenMode(false);
      setSidebarOpen(true);
      setSelectedCompany(null);
      setShowBackArrow(false);
      setNavigationHistory([]);
      setExtractedData(null);
      setIsInDataView(false);
    } else if (activeSection === 'extractions' && extractionState.isInExtractionFlow) {
      setFullscreenMode(false);
      setSidebarOpen(true);
      setIsInDataView(false);
    } else if (activeSection === 'data') {
      setIsInDataView(true);
    } else {
      setIsInDataView(false);
    }
  }, [activeSection, extractionState.isInExtractionFlow]);

  // Handle extraction complete from UploadContent
  const handleExtractionComplete = (extractedData, extractionFiles, sections) => {
    console.log('Extraction complete:', { extractedData, extractionFiles, sections });
    
    setExtractedData(extractedData);
    
    // Store extraction flow data
    setExtractionState(prev => ({
      ...prev,
      isInExtractionFlow: true,
      extractionFiles: extractionFiles || [],
      extractionSections: sections || [],
      currentProgressData: extractedData,
      extractionProgress: null,
      jobId: extractedData.jobId || null
    }));
    
    setNavigationHistory(prev => [...prev, 'progress']);
    setActiveSection('progress');
    setFullscreenMode(true);
    setSidebarOpen(false);
    setShowBackArrow(true);
    setIsInDataView(false);
    
    // Set the selected company for DataContent
    if (extractedData.companyName) {
      setSelectedCompany({
        name: extractedData.companyName,
        ticker: extractedData.tickerId || extractedData.companyName.replace(/\s+/g, '').toUpperCase(),
        sector: "Financial Services",
        isBackendCompany: extractedData.isBackendData || false
      });
    }
  };

  // Handle viewing data from progress screen
  const handleProgressViewData = (fileData) => {
    console.log('Progress view data:', fileData);
    
    // Check if this is backend data
    if (fileData.isBackendData) {
      if (!fileData.statementId || !fileData.excelPath) {
        console.error('Missing required data for viewing:', fileData);
        alert('Cannot view data: Missing statement ID or Excel path');
        return;
      }
      
      handleViewBackendData(fileData);
      return;
    }
    
    // When viewing data from progress screen, save the current extraction state
    setExtractionState(prev => ({
      ...prev,
      extractionProgress: fileData.extractions // Save current progress state
    }));
    
    setNavigationHistory(prev => [...prev, 'progress']);
    setActiveSection('data');
    setFullscreenMode(true);
    setSidebarOpen(false);
    setShowBackArrow(true);
    setIsInDataView(true);
  };

  // New function to handle backend data viewing
  const handleViewBackendData = (fileData) => {
    console.log('Viewing backend data:', fileData);
    
    // Create extracted data object for DataContent
    const extractedData = {
      jobId: fileData.jobId,
      companyName: fileData.companyName || fileData.extractedData?.companyName,
      tickerId: fileData.tickerId || fileData.extractedData?.tickerId,
      periodType: fileData.periodType || 'annual',
      sections: fileData.sections || [],
      isBackendData: true,
      jobData: fileData,
      statementId: fileData.statementId,
      statementType: fileData.statementType
    };
    
    setExtractedData(extractedData);
    setNavigationHistory(prev => [...prev, 'progress']);
    setActiveSection('data');
    setFullscreenMode(true);
    setSidebarOpen(false);
    setShowBackArrow(true);
    setIsInDataView(true);
    
    // Set the selected company
    if (extractedData.companyName) {
      setSelectedCompany({
        name: extractedData.companyName,
        ticker: extractedData.tickerId,
        sector: "Financial Services",
        isBackendCompany: true
      });
    }
  };

  // Handle back from progress screen
  const handleProgressBackToUpload = () => {
    // When clicking back from progress screen
    setExtractionState(prev => ({
      ...prev,
      isInExtractionFlow: false,
      extractionProgress: null,
      jobId: null
    }));
    setActiveSection('extractions');
    setFullscreenMode(false);
    setSidebarOpen(true);
    setShowBackArrow(false);
    setIsInDataView(false);
  };

  // Handle back from data view
  const handleBackFromData = () => {
    const lastNav = navigationHistory[navigationHistory.length - 1];
    
    if (lastNav === 'progress' || extractionState.isInExtractionFlow) {
      // If coming from extraction flow, go back to progress screen
      setActiveSection('progress');
      setFullscreenMode(true);
      setSidebarOpen(false);
      setShowBackArrow(true);
      setIsInDataView(false);
    } else if (selectedCompany) {
      // If we have a selected company but not from extraction flow, go to dashboard
      setActiveSection('dashboard');
      setFullscreenMode(false);
      setSidebarOpen(true);
      setShowBackArrow(false);
      setIsInDataView(false);
      // Clear extracted data when going back
      setExtractedData(null);
    } else {
      // If no company selected (fresh data screen), go to dashboard
      setActiveSection('dashboard');
      setFullscreenMode(false);
      setSidebarOpen(true);
      setShowBackArrow(false);
      setIsInDataView(false);
      // Clear extracted data
      setExtractedData(null);
    }
  };

  // Handle section change from sidebar
  const handleSectionChange = (section) => {
    // If in extraction flow and trying to change section, confirm
    if (extractionState.isInExtractionFlow && section !== 'extractions' && section !== 'progress') {
      if (!window.confirm('You are in an extraction flow. Leave and lose progress?')) {
        return;
      }
      // Reset extraction flow
      setExtractionState({
        isInExtractionFlow: false,
        extractionFiles: [],
        extractionSections: [],
        extractionProgress: null,
        currentProgressData: null,
        jobId: null
      });
    }
    
    setActiveSection(section);
    setIsInDataView(section === 'data');
    
    // Reset certain states when switching sections
    if (section === 'dashboard') {
      setSearchQuery('');
      setSelectedCompany(null);
      setExtractedData(null);
      setExtractionState(prev => ({
        ...prev,
        isInExtractionFlow: false,
        jobId: null
      }));
    } else if (section === 'data') {
      setFullscreenMode(false);
      setSidebarOpen(true);
    }
  };

  // Handle home click from TopNavbar
  const handleHomeClick = () => {
    setActiveSection('dashboard');
    setFullscreenMode(false);
    setSidebarOpen(true);
    setSelectedCompany(null);
    setShowBackArrow(false);
    setNavigationHistory([]);
    setExtractedData(null);
    setIsInDataView(false);
    setExtractionState({
      isInExtractionFlow: false,
      extractionFiles: [],
      extractionSections: [],
      extractionProgress: null,
      currentProgressData: null,
      jobId: null
    });
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardContent />;
      case 'extractions':
        return <SuperAdminExtractionsContent onExtractionComplete={handleExtractionComplete} />;
      case 'companies':
        return <CompaniesContent />;
      case 'users':
        return <UsersContent />;
      case 'settings':
        return <SettingsContent />;
      case 'progress':
        return (
          <ProgressScreen
            uploadedFiles={extractionState.extractionFiles}
            onViewData={handleProgressViewData}
            onReturnToUpload={handleProgressBackToUpload}
            extractionStartTime={Date.now()}
            initialExtractions={extractionState.extractionProgress}
            jobId={extractionState.jobId}
          />
        );
      case 'data':
        return (
          <DataContent 
            searchQuery={searchQuery} 
            setSearchQuery={setSearchQuery}
            extractedData={extractedData}
            fullscreenMode={fullscreenMode}
            selectedCompany={selectedCompany}
            showBackArrow={showBackArrow}
            onBackClick={handleBackFromData}
            uploadedExcelData={null}
            setUploadedExcelData={() => {}}
            activeStatement={activeStatement}
          />
        );
      default:
        return <DashboardContent />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Fixed Top Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <TopNavbar 
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeSection={activeSection}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleLogout={handleLogout}
          onCompanySelect={() => {}}
          fullscreenMode={fullscreenMode}
          onHomeClick={handleHomeClick}
          onDataClick={() => {}}
          showBackArrow={showBackArrow && (activeSection === 'data' || activeSection === 'progress')}
          onBackClick={handleBackFromData}
          userRole="superadmin"
          onExcelUpload={() => {}}
          onExcelDownload={() => {}}
          activeStatement={activeStatement}
          currentCompany={selectedCompany}
        />
      </div>
      
      {/* Sidebar and Content Container */}
      <div className="flex flex-1 pt-16">
        {/* Only show sidebar when not in fullscreen mode and not in data view */}
        {!fullscreenMode && !isInDataView && (
          <SuperAdminSidebar 
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            activeSection={activeSection}
            setActiveSection={handleSectionChange}
            userInfo={userInfo}
            fullscreenMode={fullscreenMode}
          />
        )}
        
        <div className={`flex-1 flex flex-col transition-all duration-300 ${fullscreenMode || isInDataView ? 'ml-0' : sidebarOpen ? 'ml-64' : 'ml-16'}`}>
          <main className={`flex-1 ${fullscreenMode || isInDataView ? 'p-0' : 'p-6'}`}>
            {renderContent()}
          </main>
          
          {/* Footer - Only show when not in fullscreen mode and not in data view */}
          {!fullscreenMode && !isInDataView && <Footer />}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;